import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, BadgeCheck, GraduationCap, Award, MapPin, ChevronDown, Zap } from "lucide-react";
import { scrollToId } from "../lib/scroll";

const LINES = [
  { text: "Your goals", cls: "text-slate-50" },
  { text: "+ my skills", cls: "bg-gradient-to-r from-cyan-300 to-sky-400 bg-clip-text text-transparent" },
  { text: "= real solutions.", cls: "bg-gradient-to-r from-blue-500 via-cyan-400 to-cyan-300 bg-clip-text text-transparent" },
];

const HERO_IMG =
  "https://images.pexels.com/photos/1181316/pexels-photo-1181316.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Hero() {
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 700], [0, -70]);
  const yGlow = useTransform(scrollY, [0, 700], [0, 90]);

  return (
    <section className="relative overflow-hidden" data-testid="hero-section">
      <div className="grid-bg absolute inset-0" />
      <motion.div
        style={{ y: yGlow }}
        className="pointer-events-none absolute -top-40 left-1/2 h-[560px] w-[900px] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(0,102,255,0.22),transparent)]"
      />
      <div className="pointer-events-none absolute -right-32 top-1/3 h-96 w-96 rounded-full bg-[radial-gradient(closest-side,rgba(0,240,255,0.12),transparent)]" />

      <div className="relative mx-auto grid max-w-7xl gap-14 px-5 pb-20 pt-32 sm:px-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-center lg:pb-28 lg:pt-40">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-cyan-500/25 bg-cyan-500/5 px-4 py-1.5"
            data-testid="hero-location-badge"
          >
            <MapPin size={13} className="text-cyan-400" />
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-300">
              Birmingham & West Midlands · Remote UK-wide
            </span>
          </motion.div>

          <h1 className="font-display mt-7 text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-testid="hero-headline">
            {LINES.map((l, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${l.cls}`}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.95, delay: 0.2 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {l.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-6 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base"
            data-testid="hero-subcopy"
          >
            Independent IT support & digital solutions for individuals, freelancers and small businesses.
            Practical IT skills, real-world experience — digital solutions that work, at prices that make sense.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-enquiry-button"
              onClick={() => scrollToId("enquiry")}
              className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-blue-600 px-7 py-3.5 text-sm font-semibold text-[#04101F] shadow-[0_0_32px_rgba(0,240,255,0.4)] transition-transform duration-200 hover:scale-[1.04]"
            >
              Start an enquiry
              <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-services-button"
              onClick={() => scrollToId("services")}
              className="rounded-full border border-white/15 px-7 py-3.5 text-sm font-semibold text-slate-200 transition-colors hover:border-cyan-400/50 hover:text-cyan-300"
            >
              Explore services
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-10 flex flex-wrap gap-3"
            data-testid="hero-trust-badges"
          >
            {[
              { icon: BadgeCheck, label: "MBCS — BCS Professional Member" },
              { icon: GraduationCap, label: "CertHE Computing & IT — Open University" },
              { icon: Award, label: "ICDL Advanced" },
            ].map(({ icon: Icon, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3.5 py-2 text-xs text-slate-300"
              >
                <Icon size={14} className="text-cyan-400" />
                {label}
              </span>
            ))}
          </motion.div>
        </div>

        <motion.div style={{ y: yImg }} className="relative hidden lg:block" data-testid="hero-visual">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-4 rounded-[2rem] bg-[radial-gradient(closest-side,rgba(0,240,255,0.18),transparent)] blur-xl" />
            <div
              className="relative overflow-hidden rounded-[1.6rem] border border-cyan-500/25 shadow-[0_0_60px_rgba(0,102,255,0.25)]"
              style={{ clipPath: "polygon(0 0, 100% 4%, 100% 100%, 0 96%)" }}
            >
              <img src={HERO_IMG} alt="Modern data infrastructure" className="h-[460px] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#070B19]/85 via-transparent to-[#070B19]/20" />
            </div>

            <div className="animate-float absolute -left-6 top-10 rounded-xl border border-cyan-500/30 bg-[#0D152D]/90 px-4 py-3 backdrop-blur-md" data-testid="hero-chip-price">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Services from</p>
              <p className="font-display text-xl font-bold text-cyan-300">£20/hr</p>
            </div>
            <div
              className="animate-float absolute -right-4 bottom-24 rounded-xl border border-emerald-500/30 bg-[#0D152D]/90 px-4 py-3 backdrop-blur-md"
              style={{ animationDelay: "1.4s" }}
              data-testid="hero-chip-bcs"
            >
              <p className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                <ShieldIcon /> Verified
              </p>
              <p className="font-display text-sm font-bold text-emerald-300">BCS Professional Member</p>
            </div>
            <div
              className="animate-float absolute -bottom-5 left-10 flex items-center gap-2 rounded-xl border border-blue-500/30 bg-[#0D152D]/90 px-4 py-3 backdrop-blur-md"
              style={{ animationDelay: "2.6s" }}
              data-testid="hero-chip-tagline"
            >
              <Zap size={14} className="text-blue-400" />
              <p className="text-xs font-medium text-slate-200">Small changes. Big improvements.</p>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        onClick={() => scrollToId("services")}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 text-slate-500 transition-colors hover:text-cyan-300 lg:block"
        aria-label="Scroll to services"
        data-testid="hero-scroll-cue"
      >
        <ChevronDown size={22} className="animate-bounce" />
      </motion.button>
    </section>
  );
}

const ShieldIcon = () => <BadgeCheck size={12} className="text-emerald-400" />;
